"use client";

import React from "react";
import { Flame, Clock, ShieldCheck, PhoneCall, Wrench, CircleCheck as CheckCircle2, Zap, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const keyBenefits = [{
  text: "Same-Day Tank & Tankless Replacement",
  id: "benefit-1"
}, {
  text: "Licensed, Insured & Background-Checked Plumbers",
  id: "benefit-2"
}, {
  text: "Upfront Pricing With Zero Hidden Fees",
  id: "benefit-3"
}, {
  text: "10-Year Manufacturer Warranty Options",
  id: "benefit-4"
}];
const quickStats = [{
  label: "Emergency Response",
  value: "30-60 Min",
  icon: Zap
}, {
  label: "Installations Done",
  value: "2,500+",
  icon: Wrench
}, {
  label: "Customer Satisfaction",
  value: "100%",
  icon: ShieldCheck
}];
export default function WaterHeaterHero() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="waterheaterhero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Subtle decorative background glow */}
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Main Hero Copy */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="lg:col-span-7 flex flex-col items-start space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5">
                <Flame className="size-3.5 text-accent fill-accent" />
                Emergency Water Heater Service
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-xs font-medium gap-1.5 text-muted-foreground">
                <Clock className="size-3.5 text-primary" />
                24/7 Available in Jonesboro, GA
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground font-default leading-[1.1]">
                Fast, Reliable <span className="text-primary">Water Heater</span> Replacement & Repair
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                No hot water? Leaking tank? Jonesboro Emergency Plumber delivers rapid same-day water heater replacements and diagnostics. We install energy-efficient tank and tankless systems with total warranty protection.
              </p>
            </div>

            {/* Checklist of value propositions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full pt-1">
              {keyBenefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-5 text-accent shrink-0" />
                  <span className="text-sm font-medium text-foreground">{benefit.text}</span>
                </div>)}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 h-12 shadow-md">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-5" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-6 font-medium border-border hover:bg-muted">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Schedule Free Estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Micro stats banner */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border w-full">
              {quickStats.map((stat, index) => {
              const IconComponent = stat.icon;
              return <div key={stat.label} data-index={index} className="space-y-1">
                    <div className="flex items-center gap-1.5 text-primary">
                      <IconComponent className="size-4 shrink-0" />
                      <span className="text-base sm:text-lg font-bold text-foreground">{stat.value}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-tight">{stat.label}</p>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Hero Visual Card / Side Graphic */}
          <motion.div initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto w-full max-w-md lg:max-w-none">
              
              {/* Main Card with Image & Floating Tag */}
              <Card className="overflow-hidden border-border bg-card shadow-xl p-0">
                <div className="relative aspect-4/3 w-full bg-muted overflow-hidden">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_technician_installation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_technician_installation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_technician_installation.png-wvc-sizes" alt="Plumber inspecting and installing a new energy-efficient water heater" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                  
                  {/* Floating Price Match / Trust Badge */}
                  <div className="absolute top-4 right-4 bg-background/95 backdrop-blur-md rounded-lg p-3 shadow-md border border-border">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="size-5 text-accent" />
                      <div>
                        <p className="text-xs font-semibold text-foreground leading-none">100% Guaranteed</p>
                        <p className="text-[11px] text-muted-foreground leading-none mt-1">Parts & Labor</p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-xs font-semibold uppercase tracking-wider text-accent">Ready For Immediate Dispatch</p>
                    <p className="text-sm font-medium text-white/95">Jonesboro, Riverdale, Morrow & Clayton County</p>
                  </div>
                </div>

                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-card-foreground font-default">Need An Immediate Replacement?</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        We stock leading brands including Rheem, Bradford White, and Navien tankless units.
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 bg-secondary/70 rounded-lg border border-border flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                        <Flame className="size-5" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-secondary-foreground">Tankless Upgrade Special</p>
                        <p className="text-[11px] text-muted-foreground">Endless hot water & lower utility bills</p>
                      </div>
                    </div>
                    <Button asChild size="sm" variant="default" className="text-xs font-semibold">
                      <Link to="/contact/">Inquire</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}